<template>
  <div class="chg-addr-preset-efiling">
    <v-card rounded="md" class="chg-addr-preset-card">
      <v-card-text>
        <div class="change-address-wrapper">
          <!-- Header -->
          <div class="d-flex justify-space-between align-center mb-4">
            <h3 class="section-title mb-0">Change of Registered Office Address</h3>
          </div>

          <!-- Entity Information -->
          <div class="info-section mb-6">
            <div class="info-section-title">Entity Information</div>
            <v-row class="ma-0">
              <v-col cols="12" sm="6" class="pa-0 pr-sm-3 mb-3">
                <label class="field-label">Entity Name <span class="text-error">*</span></label>
                <v-text-field
                  v-model="formModel.companyName"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  :readonly="readOnly"
                />
              </v-col>
              <v-col cols="12" sm="6" class="pa-0 pl-sm-3 mb-3">
                <label class="field-label">Registration No. (UEN) <span class="text-error">*</span></label>
                <v-text-field
                  v-model="formModel.companyRegistrationNo"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  :readonly="readOnly"
                />
              </v-col>
            </v-row>
          </div>

          <!-- New Registered Office Address -->
          <div class="info-section mb-6">
            <div class="info-section-title">New Registered Office Address</div>

            <!-- Postal Code + Retrieve -->
            <v-row class="ma-0">
              <v-col cols="12" sm="6" class="pa-0 pr-sm-3 mb-3">
                <label class="field-label">Postal Code <span class="text-error">*</span></label>
                <v-text-field
                  v-model="formModel.officeAddr.postalCode"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  :readonly="readOnly"
                  :loading="isSearchingPostalCode"
                >
                  <template #prepend-inner>
                    <span class="postal-prefix">S</span>
                  </template>
                  <template v-if="!readOnly" #append-inner>
                    <v-btn
                      variant="tonal"
                      color="primary"
                      size="small"
                      density="compact"
                      :loading="isSearchingPostalCode"
                      :disabled="!formModel.officeAddr.postalCode?.trim()"
                      @click="searchPostalCode"
                    >
                      Retrieve address
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>

            <v-alert
              v-if="postalCodeError"
              type="error"
              density="compact"
              class="mb-3"
              closable
              @click:close="postalCodeError = null"
            >
              {{ postalCodeError }}
            </v-alert>

            <!-- Retrieved address line -->
            <div v-if="addressDisplayLine" class="retrieved-address-line mb-4">
              {{ addressDisplayLine }}
            </div>

            <!-- Level / Unit -->
            <v-row class="ma-0 align-center">
              <v-col cols="12" sm="4" class="pa-0 pr-sm-3 mb-3">
                <label class="field-label">Level</label>
                <v-text-field
                  v-model="formModel.officeAddr.levelNo"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  :readonly="readOnly"
                  :disabled="!formModel.officeAddr.haveLevelUnit"
                />
              </v-col>
              <v-col cols="12" sm="4" class="pa-0 px-sm-3 mb-3">
                <label class="field-label">Unit</label>
                <v-text-field
                  v-model="formModel.officeAddr.unitNo"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  :readonly="readOnly"
                  :disabled="!formModel.officeAddr.haveLevelUnit"
                />
              </v-col>
            </v-row>
            <v-row v-if="!readOnly" class="ma-0">
              <v-col cols="12" class="pa-0">
                <v-checkbox
                  v-model="noLevelUnit"
                  label="Address doesn't have level and unit"
                  density="compact"
                  hide-details
                  color="primary"
                />
              </v-col>
            </v-row>
          </div>

          <!-- Effective Date -->
          <div class="info-section">
            <div class="info-section-title">Effective Date</div>
            <v-row class="ma-0">
              <v-col cols="12" sm="6" class="pa-0 mb-3">
                <label class="field-label">Effective Date of Change <span class="text-error">*</span></label>
                <v-menu
                  v-model="datedMenu"
                  :close-on-content-click="false"
                  location="bottom"
                  :disabled="readOnly"
                >
                  <template #activator="{ props: menuProps }">
                    <v-text-field
                      v-bind="menuProps"
                      v-model="datedDisplay"
                      :placeholder="datedPlaceholder"
                      variant="outlined"
                      density="compact"
                      hide-details="auto"
                      readonly
                      append-inner-icon="mdi-calendar"
                      :disabled="readOnly"
                    />
                  </template>
                  <v-date-picker
                    v-bind="datedPickerProps"
                    v-model="datedPickerValue"
                    @update:model-value="datedMenu = false"
                  />
                </v-menu>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-card-text>

      <v-divider v-if="!readOnly" />
      <v-card-actions v-if="!readOnly" class="justify-end pa-4 pr-8">
        <v-btn
          v-if="showBackButton"
          variant="outlined"
          color="primary"
          :disabled="isSubmitting || isValidating"
          @click="emit('back')"
        >
          {{ t('startTask.Back') || 'Back' }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="isSubmitting || isValidating"
          :disabled="isSubmitting || isValidating"
          @click="handleSubmit"
        >
          {{ t('startTask.Submit') || 'Submit' }}
        </v-btn>
      </v-card-actions>
      <v-card-actions v-else class="justify-center pa-4">
        <v-alert type="success" variant="tonal" density="compact" class="mb-0">
          {{ t('startTask.E-Filing has been completed') || 'E-Filing has been completed' }}
        </v-alert>
      </v-card-actions>
    </v-card>

    <!-- SG 파이썬 데몬 로그인 → BM02C save-draft (/tilkoapi/bizfile/office-address/save-draft) -->
    <SgPyLoginDialog
      v-model="showLoginDialog"
      :task-id="taskIdResolved"
      :filing-agent-u-e-n="singpassFilingAgentUEN"
      @authenticated="onAuthenticated"
    />

    <v-dialog v-model="showResultDialog" max-width="450" persistent>
      <v-card>
        <v-card-title class="d-flex align-center pa-4">
          <v-icon
            :color="resultDialogType === 'success' ? 'success' : 'error'"
            size="28"
            class="mr-2"
          >
            {{ resultDialogType === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
          </v-icon>
          <span>
            {{
              resultDialogType === 'success'
                ? t('startTask.E-Filing Complete') || 'E-Filing Complete'
                : t('startTask.E-Filing Error') || 'E-Filing Error'
            }}
          </span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <p :class="resultDialogType === 'success' ? 'text-success' : 'text-error'">
            {{ resultDialogMessage }}
          </p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-end pa-4">
          <v-btn
            :color="resultDialogType === 'success' ? 'success' : 'primary'"
            variant="flat"
            @click="closeResultDialog"
          >
            {{ t('startTask.OK') || 'OK' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { useDialog } from '@/shared/composables/useDialog';
import { apiClient } from '@/shared/api/client';
import { entityApi } from '@/features/Entity/api/entityApi';
import { useDateField } from '@/shared/composables/dateUtils';
import SgPyLoginDialog from '@/features/startTask/preset_task/sg/components/change-financial-year-end/SgPyLoginDialog.vue';
import { saveEFilingData, updateEFilingComplete } from '@/features/startTask/api/taskApi';
import {
  createEmptyChangeAddressForm,
  buildChangeAddressEfilingPayload,
  buildChangeAddressDaemonOfficeAddressList,
  buildChangeAddressTaskFormData,
  type ChangeAddressFormModel,
} from './composables/useFormPayload';
import { parseEfilingFormDataToChangeAddressModel } from './composables/parseEfilingFormDataToChangeAddressModel';

function normalizeUen(raw: string): string {
  return String(raw || '').replace(/\s/g, '').trim();
}

interface Props {
  taskData?: any;
  taskId?: string;
  entityCd?: string;
  showBackButton?: boolean;
  readOnly?: boolean;
  taskConfig?: any;
  embedded?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  taskData: null,
  taskId: undefined,
  entityCd: '',
  showBackButton: true,
  readOnly: false,
  taskConfig: null,
  embedded: false,
});

const emit = defineEmits<{
  back: [];
  submit: [data: any];
  complete: [];
  error: [message: string];
}>();

const languageStore = useLanguageStore();
const sessionStore = useSessionStore();
const { createSyncT } = useTranslation();
const { warning: showWarning } = useDialog();
const t = createSyncT(languageStore.currentLanguage);

const taskIdResolved = computed(() => props.taskId || '');
const formModel = ref<ChangeAddressFormModel>(createEmptyChangeAddressForm());
const isSubmitting = ref(false);
const isValidating = ref(false);

// --- Date field ---
const datedField = useDateField();
const datedMenu = datedField.dateMenu;
const datedDisplay = datedField.dateDisplay;
const datedPickerValue = datedField.datePickerValue;
const datedPlaceholder = datedField.datePlaceholder;
const datedPickerProps = datedField.datePickerProps;

function normalizeIso(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

watch(() => formModel.value.dated, (v) => {
  const s = normalizeIso(v);
  if (datedField.dateValue.value !== s) datedField.dateValue.value = s;
}, { immediate: true });

watch(datedField.dateValue, (v) => {
  const s = normalizeIso(v);
  if (normalizeIso(formModel.value.dated) !== s) formModel.value.dated = s;
});

// --- Postal code search ---
const isSearchingPostalCode = ref(false);
const isPostalCodeSearched = ref(false);
const postalCodeError = ref<string | null>(null);

const noLevelUnit = computed({
  get: () => !formModel.value.officeAddr.haveLevelUnit,
  set: (v: boolean) => {
    formModel.value.officeAddr.haveLevelUnit = !v;
    if (v) {
      formModel.value.officeAddr.levelNo = '';
      formModel.value.officeAddr.unitNo = '';
    }
  },
});

const addressDisplayLine = computed(() => {
  const a = formModel.value.officeAddr;
  const parts = [a.blkHseNo, a.streetName, a.buildingName].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '';
});

const searchPostalCode = async () => {
  const code = formModel.value.officeAddr.postalCode?.trim();
  if (!code) return;

  isSearchingPostalCode.value = true;
  postalCodeError.value = null;

  try {
    const response = await entityApi.searchPostalCode(code);
    const result = (response as any).data?.data || (response as any).data;
    if (result) {
      formModel.value.officeAddr.streetName = result.streetName || '';
      formModel.value.officeAddr.buildingName = result.buildingEstateName || '';
      formModel.value.officeAddr.blkHseNo = result.blockHouseNo || '';
      isPostalCodeSearched.value = true;
    }
  } catch (error: any) {
    const responseData = error?.response?.data;
    let errorMessage = '';
    if (responseData?.error) {
      errorMessage = typeof responseData.error === 'object'
        ? responseData.error.message || ''
        : String(responseData.error);
    }
    postalCodeError.value = errorMessage || responseData?.message || error?.message || 'Postal code search failed';
  } finally {
    isSearchingPostalCode.value = false;
  }
};

// --- Singpass (SG 파이썬 데몬 세션) ---
const showLoginDialog = ref(false);
const singpassEFilingData = ref<any>(null);
const singpassEntityUEN = ref('');
const singpassFilingAgentUEN = ref('');
const singpassEntityName = ref('');

const showResultDialog = ref(false);
const resultDialogType = ref<'success' | 'error'>('success');
const resultDialogMessage = ref('');

function applyFormDataFromParent() {
  const fd = props.taskData?.formData ?? props.taskData;
  if (fd && typeof fd === 'object' && (fd.company_name || fd.companyName || fd.officeAddr)) {
    formModel.value = parseEfilingFormDataToChangeAddressModel(fd as Record<string, any>);
  }
}

async function loadEfilingFromDb() {
  const tid = taskIdResolved.value;
  if (!tid) return;
  try {
    const efRes = await apiClient.get(`/startTask/tasks/${tid}/efiling-data`);
    const item = (efRes as any)?.data?.item ?? null;

    const fd = item?.FORM_DATA;
    if (fd && typeof fd === 'object' && Object.keys(fd).length > 0) {
      formModel.value = parseEfilingFormDataToChangeAddressModel(fd);
    } else {
      applyFormDataFromParent();
    }
  } catch (e) {
    console.warn('[ChangeAddressEFiling] efiling-data load failed', e);
    applyFormDataFromParent();
  }
}

watch(
  () => taskIdResolved.value,
  (id) => { if (id) void loadEfilingFromDb(); },
  { immediate: true },
);

watch(
  () => props.taskData,
  () => {
    const m = formModel.value;
    const empty = !m.companyName.trim() && !m.officeAddr.postalCode.trim();
    if (empty) applyFormDataFromParent();
  },
  { deep: true },
);

async function handleSubmit() {
  if (props.readOnly || isSubmitting.value || isValidating.value) return;
  const tid = taskIdResolved.value;
  if (!tid) {
    void showWarning(
      t('startTask.Error') || 'Error',
      t('startTask.Task ID is required') || 'Task ID is required.',
    );
    return;
  }

  const customer = sessionStore.getCustomerInfo();
  const filingAgentUen = normalizeUen(customer?.regNo || '');
  const entityUen = normalizeUen(formModel.value.companyRegistrationNo);

  const { eFilingDataForApi } = buildChangeAddressEfilingPayload(
    formModel.value,
    entityUen,
    filingAgentUen,
  );

  const addr = formModel.value.officeAddr;
  const formFields = {
    entity: normalizeUen(props.entityCd) || entityUen,
    postalCode: addr.postalCode.trim(),
    blkHseNo: addr.blkHseNo.trim(),
    streetName: addr.streetName.trim(),
    buildingName: addr.buildingName.trim(),
    levelNo: addr.levelNo.trim(),
    unitNo: addr.unitNo.trim(),
    haveLevelUnit: addr.haveLevelUnit,
    effectiveDateOfChange: formModel.value.dated.trim(),
  };

  isSubmitting.value = true;
  try {
    isValidating.value = true;
    await apiClient.put(`/startTask/change-address/update-form-data/${tid}`, {
      formData: { formFields },
      eFilingDataForApi,
    });

    const response = await apiClient.get(`/startTask/change-address/validate/${tid}`);
    const res = response as any;
    const isValid = res?.data?.isValid === true || res?.data?.data?.isValid === true;

    if (!isValid) {
      await showWarning(
        t('startTask.Validation Error') || 'Validation Error',
        t('startTask.Please check the required fields and enter the values') ||
          'Please check the required fields.',
      );
      return;
    }

    singpassEFilingData.value = eFilingDataForApi;
    singpassEntityUEN.value = String((eFilingDataForApi as any).EntityUEN ?? entityUen);
    singpassFilingAgentUEN.value = String((eFilingDataForApi as any).FilingAgentUEN ?? filingAgentUen);
    singpassEntityName.value = formModel.value.companyName;
    showLoginDialog.value = true;
  } catch (error: any) {
    console.error('[ChangeAddressEFiling] submit', error);
    await showWarning(
      t('startTask.Error') || 'Error',
      error?.message || t('startTask.Failed to save or validate') || 'Failed to save or validate.',
    );
    emit('error', error?.message || String(error));
  } finally {
    isValidating.value = false;
    isSubmitting.value = false;
  }
}

function onAuthenticated(payload: { sessionId: string; mstIdx?: number | null; filingAgentUEN?: string }) {
  showLoginDialog.value = false;
  void executeSaveDraft(payload);
}

/** SG 데몬 BM02C save-draft 호출 → L_TASK_EFILING_DATA 저장 + Task 완료 처리. */
async function executeSaveDraft(auth: { sessionId: string; mstIdx?: number | null; filingAgentUEN?: string }) {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  const tid = taskIdResolved.value;
  const filingAgentUen = normalizeUen(auth.filingAgentUEN || singpassFilingAgentUEN.value);
  const officeAddressList = buildChangeAddressDaemonOfficeAddressList(formModel.value);
  const requestPayload = {
    entityUEN: singpassEntityUEN.value,
    entityName: singpassEntityName.value,
    filingAgentUEN: filingAgentUen,
    officeAddressList,
  };
  try {
    const res: any = await apiClient.post('/tilkoapi/bizfile/office-address/save-draft', {
      sessionId: auth.sessionId,
      mstIdx: auth.mstIdx ?? null,
      ...requestPayload,
    });
    const data = res?.data?.data || res?.data || {};
    const draftId = String(data.draft_id || '');

    if (tid) {
      const nowIso = new Date().toISOString();
      try {
        await saveEFilingData(tid, {
          taskTypeCd: 'B32105',
          entityCd: props.entityCd || null,
          formData: buildChangeAddressTaskFormData(formModel.value, props.entityCd || ''),
          requestPayload,
          responseData: { draftId, result: data.result || null },
          status: 'B36003',
          isSuccess: true,
          submittedAt: nowIso,
          completedAt: nowIso,
          isDraft: false,
        });
      } catch (e2) { console.warn('[ChangeAddressEFiling] saveEFilingData failed', e2); }
      try { await updateEFilingComplete(tid); } catch (e3) { console.warn('[ChangeAddressEFiling] updateEFilingComplete failed', e3); }
    }

    await finalizeSuccess({ data: { draftId, result: data.result || null } });
  } catch (e: any) {
    handleSingpassError({
      message: e?.response?.data?.error?.message || e?.response?.data?.message || e?.message,
    });
  } finally {
    isSubmitting.value = false;
  }
}

function handleSingpassError(error: any) {
  showLoginDialog.value = false;
  resultDialogType.value = 'error';
  resultDialogMessage.value =
    error?.message ||
    t('startTask.E-Filing failed') ||
    'E-Filing failed. Please try again.';
  showResultDialog.value = true;
  emit('error', resultDialogMessage.value);
}

async function finalizeSuccess(apiResponse: any) {
  const apiData = apiResponse?.data ?? apiResponse;
  resultDialogType.value = 'success';
  resultDialogMessage.value =
    t('startTask.E-Filing completed successfully') ||
    'E-Filing has been completed successfully.';
  showResultDialog.value = true;
  emit('submit', { eFilingData: singpassEFilingData.value, apiResponse: apiData });
}

function closeResultDialog() {
  showResultDialog.value = false;
  if (resultDialogType.value === 'success') {
    emit('complete');
  }
}
</script>

<style scoped lang="scss">
* {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.chg-addr-preset-efiling {
  min-height: 100%;
  width: 100%;
}

.chg-addr-preset-card {
  margin: 16px;
  max-width: 100%;
  box-sizing: border-box;
}

.chg-addr-preset-card :deep(.v-card-text) {
  max-width: 100%;
  box-sizing: border-box;
}

.chg-addr-preset-card :deep(.v-row) {
  margin-left: 0;
  margin-right: 0;
}

.chg-addr-preset-card :deep(.v-col) {
  padding-left: 8px;
  padding-right: 8px;
}

.chg-addr-preset-card :deep(.v-card-actions) {
  padding-right: 32px !important;
}

.change-address-wrapper {
  margin-bottom: 24px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #3C4650;
}

.info-section {
  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 20px 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.info-section-title {
  font-size: 16px;
  font-weight: 700;
  color: #3C4650;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.field-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #3C4650;
  margin-bottom: 6px;
}

.postal-prefix {
  font-weight: 600;
  color: #3C4650;
  margin-right: 2px;
}

.retrieved-address-line {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 14px;
  color: #3C4650;
  line-height: 1.5;
}

:deep(.v-label),
:deep(.v-field-label) {
  font-size: 14px !important;
  color: #3C4650;
}

:deep(.v-field__input) {
  font-size: 14px !important;
  color: #3C4650;
}

:deep(.v-field__input::placeholder),
:deep(input::placeholder) {
  font-size: 14px !important;
  color: #777E85 !important;
}

:deep(.v-btn) {
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: none;
}

:deep(.v-btn--color-primary) {
  background-color: #1B84FF !important;
}

.text-error {
  color: rgb(var(--v-theme-error));
}
</style>
